import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import BookCard from './BookCard';
import { getRecentlyAddedBooks } from '../services/BookService';
import { Book } from '../services/BookService';

interface RecentlyAddedBooksProps {
  limit?: number;
  onBookClick?: (bookId: number) => void;
}

interface ApiResponse {
  books?: Book[];
  [key: string]: any;
}

const RecentlyAddedBooks: React.FC<RecentlyAddedBooksProps> = ({ 
  limit = 6,
  onBookClick 
}) => {
  const [books, setBooks] = useState<Book[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchRecentBooks = async () => {
      try {
        setLoading(true);
        const data = await getRecentlyAddedBooks(limit) as Book[] | ApiResponse;
        
        // Verificăm dacă datele returnate sunt un array sau sunt învelite într-un obiect
        const booksData = Array.isArray(data) ? data : data.books || [];
        
        setBooks(booksData);
        setError(null);
      } catch (err) {
        console.error('Eroare la încărcarea cărților recente:', err);
        setError('Nu s-au putut încărca cărțile recente. Vă rugăm încercați din nou mai târziu.');
      } finally {
        setLoading(false);
      }
    };

    fetchRecentBooks();
    
    // Opțional: Configurăm un interval pentru a actualiza lista la fiecare 5 minute
    const intervalId = setInterval(fetchRecentBooks, 5 * 60 * 1000);
    
    // Curățăm intervalul la dezmontarea componentei
    return () => clearInterval(intervalId);
  }, [limit]);

  const handleBookCardClick = (bookId: number) => {
    if (onBookClick) {
      onBookClick(bookId);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 p-4 rounded-lg text-center">
        <p className="text-red-600">{error}</p>
        <button 
          onClick={() => window.location.reload()}
          className="mt-2 px-4 py-1 bg-red-100 text-red-800 rounded-lg hover:bg-red-200"
        >
          Reîncarcă
        </button>
      </div>
    );
  }

  if (books.length === 0) {
    return (
      <div className="bg-gray-50 p-6 rounded-lg text-center">
        <p className="text-gray-600">Nu există cărți adăugate recent.</p>
      </div>
    );
  }

  return (
    <div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
        {books.map(book => (
          <div key={book.id} onClick={() => handleBookCardClick(book.id)}>
            <BookCard 
              id={book.id}
              title={book.title}
              author={book.author}
              description={book.description || 'Fără descriere disponibilă'}
              coverImage={book.coverImage || ''}
              category={book.category || 'Necategorizat'}
              fileName={book.fileName}
              fileType={book.fileType}
            />
          </div>
        ))}
      </div>
      
      <div className="text-center mt-12">
        <Link 
          to="/books" 
          className="inline-block px-8 py-3 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition shadow-md"
        >
          Vezi toate cărțile
        </Link>
      </div>
    </div>
  );
};

export default RecentlyAddedBooks; 